import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Banner, PageLoading, Select } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import { displayCell, exportCsv, exportXlsx } from './reportExport';

import type { ReportColumn, ReportResponse, TeamMember } from '../../types';

/**
 * A build-your-own tabular report.
 *
 * The same screen serves visits and orders (and whatever comes next): the
 * server hands back the full column catalogue — system columns plus the
 * company's own custom form fields — and every row's values for all of
 * them. This screen is the chrome: date range and filters, a Zoho-style
 * column picker, the table, and CSV / Excel export. Which columns are
 * shown is a per-person preference, kept in the browser, so it survives a
 * refresh without a save button or a trip to the server.
 */
interface StatusOption { value: string; label: string; }

interface Props {
  title: string;
  subtitle: string;
  endpoint: string;       // e.g. '/reports/visits'
  storageKey: string;     // localStorage key for the chosen columns
  exportName: string;     // base filename
  defaultColumns: string[];
  statusOptions: StatusOption[];
  statusLabel?: string;
}

function firstOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function today(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function loadChosen(key: string, fallback: string[]): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.every((x) => typeof x === 'string')) return arr;
    }
  } catch { /* ignore */ }
  return fallback;
}

export default function ReportView({
  title, subtitle, endpoint, storageKey, exportName,
  defaultColumns, statusOptions, statusLabel = 'Status',
}: Props) {
  const toast = useToast();

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('');

  const [data, setData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [salesmen, setSalesmen] = useState<TeamMember[]>([]);
  const [chosen, setChosen] = useState<string[]>(() => loadChosen(storageKey, defaultColumns));
  const [customizing, setCustomizing] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ReportResponse>(endpoint, {
        from, to, userId: userId || undefined, status: status || undefined,
      });
      setData(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not run the report.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, from, to, userId, status]);

  useEffect(() => { void run(); /* first load */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get<TeamMember[]>('/users')
      .then((rows) => setSalesmen(rows.filter((u) => u.role === 'sales_rep' || u.role === 'manager')))
      .catch(() => { /* filter is optional */ });
  }, []);

  const catalogue = data?.columns ?? [];
  const byKey = useMemo(
    () => new Map(catalogue.map((c) => [c.key, c])), [catalogue],
  );

  // Only keep chosen keys that actually exist in this report's catalogue.
  const visibleColumns = useMemo<ReportColumn[]>(
    () => chosen.map((k) => byKey.get(k)).filter((c): c is ReportColumn => !!c),
    [chosen, byKey],
  );

  const setChosenPersist = useCallback((next: string[]) => {
    setChosen(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
  }, [storageKey]);

  const rows = data?.rows ?? [];
  const columnsForExport = visibleColumns.length ? visibleColumns : catalogue;

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={(
          <>
            <button type="button" className="btn btn-ghost"
              onClick={() => setCustomizing(true)} disabled={!catalogue.length}>
              <Icon name="sliders" size={16} /> Columns
              {visibleColumns.length ? ` (${visibleColumns.length})` : ''}
            </button>
            <button type="button" className="btn btn-ghost"
              onClick={() => exportCsv(exportName, columnsForExport, rows)}
              disabled={!rows.length}>
              <Icon name="download" size={16} /> CSV
            </button>
            <button type="button" className="btn btn-primary"
              onClick={() => { void exportXlsx(exportName, columnsForExport, rows); }}
              disabled={!rows.length}>
              <Icon name="download" size={16} /> Excel
            </button>
          </>
        )}
      />

      <div className="page col gap-16">
        <div className="card" style={{ padding: 14 }}>
          <div className="row gap-12" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ margin: 0 }}>
              <label>From</label>
              <input type="date" className="input" value={from}
                onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>To</label>
              <input type="date" className="input" value={to}
                onChange={(e) => setTo(e.target.value)} />
            </div>
            <div style={{ minWidth: 170 }}>
              <Select label="Salesman" value={userId}
                onChange={(e) => setUserId(e.target.value)}>
                <option value="">Everyone</option>
                {salesmen.map((u) => (
                  <option key={u.id} value={u.id}>{u.fullName}</option>
                ))}
              </Select>
            </div>
            {statusOptions.length ? (
              <div style={{ minWidth: 160 }}>
                <Select label={statusLabel} value={status}
                  onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All</option>
                  {statusOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </div>
            ) : null}
            <button type="button" className="btn btn-primary" onClick={() => void run()}>
              Run report
            </button>
          </div>
        </div>

        {data?.capped ? (
          <Banner kind="warning">
            Showing the first {data.rowCap.toLocaleString('en-IN')} rows. Narrow the date
            range or filter by salesman to see the rest.
          </Banner>
        ) : null}

        {loading ? <PageLoading /> : !visibleColumns.length ? (
          <div className="card"><div className="empty">
            <Icon name="list" size={30} strokeWidth={1.5} />
            <h3 style={{ marginTop: 10 }}>No columns chosen</h3>
            <p className="small">Pick the columns you want with the <strong>Columns</strong> button.</p>
          </div></div>
        ) : rows.length === 0 ? (
          <div className="card"><div className="empty">
            <Icon name="list" size={30} strokeWidth={1.5} />
            <h3 style={{ marginTop: 10 }}>Nothing in this range</h3>
            <p className="small">No records match these dates and filters.</p>
          </div></div>
        ) : (
          <div className="card">
            <div className="row between" style={{ padding: '10px 14px' }}>
              <span className="small faint">
                {rows.length.toLocaleString('en-IN')} row{rows.length === 1 ? '' : 's'}
                {' · '}{visibleColumns.length} column{visibleColumns.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    {visibleColumns.map((c) => (
                      <th key={c.key}
                        className={['number', 'money'].includes(c.type) ? 'num' : undefined}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {visibleColumns.map((c) => (
                        <td key={c.key}
                          className={['number', 'money'].includes(c.type) ? 'num nowrap' : undefined}>
                          {displayCell(c.type, row[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {customizing ? (
        <ColumnCustomizer
          catalogue={catalogue}
          chosen={chosen}
          onChange={setChosenPersist}
          onReset={() => setChosenPersist(defaultColumns)}
          onClose={() => setCustomizing(false)}
        />
      ) : null}
    </>
  );
}

// =====================================================================

function ColumnCustomizer({
  catalogue, chosen, onChange, onReset, onClose,
}: {
  catalogue: ReportColumn[];
  chosen: string[];
  onChange: (next: string[]) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const byKey = useMemo(() => new Map(catalogue.map((c) => [c.key, c])), [catalogue]);
  const chosenSet = new Set(chosen);

  // Available = everything not already chosen, grouped, filtered by search.
  const q = search.trim().toLowerCase();
  const groups = useMemo(() => {
    const out = new Map<string, ReportColumn[]>();
    for (const col of catalogue) {
      if (chosenSet.has(col.key)) continue;
      if (q && !col.label.toLowerCase().includes(q) && !col.group.toLowerCase().includes(q)) continue;
      const arr = out.get(col.group) ?? [];
      arr.push(col);
      out.set(col.group, arr);
    }
    return [...out.entries()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, chosen, q]);

  const add = (key: string) => onChange([...chosen, key]);
  const remove = (key: string) => onChange(chosen.filter((k) => k !== key));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= chosen.length) return;
    const next = [...chosen];
    const a = next[i];
    const b = next[j];
    if (a === undefined || b === undefined) return;
    next[i] = b;
    next[j] = a;
    onChange(next);
  };

  return (
    <Modal
      title="Customize columns"
      subtitle="Pick and order the columns for this report."
      onClose={onClose}
      extraWide
      footer={(
        <>
          <button type="button" className="btn btn-ghost" onClick={onReset}>Reset to default</button>
          <span className="grow" />
          <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
        </>
      )}
    >
      <div className="report-cols">
        <div className="report-cols-side">
          <div className="report-cols-head">AVAILABLE COLUMNS</div>
          <input className="input" type="search" placeholder="Search columns"
            style={{ marginBottom: 8 }}
            value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="report-cols-list">
            {groups.length === 0 ? (
              <div className="small faint" style={{ padding: 12 }}>Nothing left to add.</div>
            ) : groups.map(([group, cols]) => (
              <div key={group}>
                <div className="report-cols-group">{group}</div>
                {cols.map((col) => (
                  <button type="button" key={col.key} className="report-col-opt"
                    onClick={() => add(col.key)}>
                    <Icon name="plus" size={14} />
                    <span>{col.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="report-cols-side">
          <div className="report-cols-head">SELECTED COLUMNS ({chosen.length})</div>
          <div className="report-cols-list">
            {chosen.length === 0 ? (
              <div className="small faint" style={{ padding: 12 }}>
                No columns yet — add some from the left.
              </div>
            ) : chosen.map((key, i) => {
              const col = byKey.get(key);
              if (!col) return null;
              return (
                <div key={key} className="report-col-chosen">
                  <span className="grow">{col.label}
                    <span className="faint small"> · {col.group}</span>
                  </span>
                  <button type="button" className="icon-btn" title="Move up"
                    disabled={i === 0} onClick={() => move(i, -1)}>
                    <Icon name="chevron-left" size={15} />
                  </button>
                  <button type="button" className="icon-btn" title="Move down"
                    disabled={i === chosen.length - 1} onClick={() => move(i, 1)}>
                    <Icon name="chevron-right" size={15} />
                  </button>
                  <button type="button" className="icon-btn" title="Remove"
                    onClick={() => remove(key)}>
                    <Icon name="x" size={15} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
